import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Tooltip } from 'components/atoms';
import { AppLayout } from 'layouts';
import { useDebounce, useFetch } from 'hooks';
import FilterContext from 'context/third-party-filter';
import { headings as headingsConfig } from './config';
import {
  Abbreviation,
  Button,
  Callout,
  Country,
  Date,
  Link,
  PageTitle,
  RiskRating,
} from 'components/atoms';
import { ActionMenu, Indicator, Pagination, Search, Table } from 'components/molecules';
import {
  PaginationWrapper,
  ThirdPartyName,
  ThirdPartyDetail,
  ChildContainer,
  ActionMenuWrapper,
  ThirdpartyNameLink,
} from './ThirdParty.styles';
import { useAuth } from 'auth';
import { useConfig } from 'config';
import { GatewayPermissions } from 'auth/constants/permissions';
import ReportActionsDrawer from './report-actions-drawer/ReportsActionsDrawer';
import { ActionMenuAction } from 'components/molecules/action-menu/types';
import ThirdPartyDeleteModal from './third-party-delete-modal';
import { ButtonToggle } from 'components/atoms/button/Button.styles';
import { RegisteredClientUsers, ThirdpartySearchResponse } from './types';
import UsersCounter from 'components/organisms/users-counter';
import { ellipsisText } from 'helpers/strings-functions';
import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from 'services/applicationInsightsService';
import useDeepState from 'hooks/useDeepState';
import { useCountryHelper } from 'helpers/countriesHelper';
import { ThirdPartyType } from 'types';
import { useUserSettings, useTerminologyConfig } from 'context/DataContext';
import ThirdPartyLinkageDetails from './third-party-linkage-details';

const ThirdParty = () => {
  const [sortingIndex, setSortingIndex] = useState(-1);
  const [isDescending, setIsDescending] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [filters, setFilters] = useDeepState({
    pageNumber: 1,
  });
  const { tokenInfo } = useAuth();
  const { hasPermission, isLinkedThirdpartiesFeatureEnabled } = useUserSettings();
  const terminologyConfig = useTerminologyConfig();
  const { vantageWebApi } = useConfig();
  const history = useHistory();
  const isAdmin: boolean = hasPermission(GatewayPermissions.Admin);
  const [showAll, setShowAll] = useState<boolean>(isAdmin);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const { sortedCountries } = useCountryHelper();
  const storedPreference = JSON.parse(localStorage.getItem('show-actions-guide-tooltip'));
  const [tooltipVisible, setTooltipVisible] = useState(
    storedPreference !== null ? storedPreference : true
  );
  const [thirdPartyToBeDeleted, setThirdPartyToBeDelete] = useState<string>(null);
  const [resultData, setResultData] = useState<ThirdpartySearchResponse['results']>([]);
  const [selectedFiltersCount, setSelectedFiltersCount] = useState<{
    [key: string]: number;
  }>({});

  const {
    data: usersData,
    loading: usersLoading,
    error: usersError,
  } = useFetch(`${vantageWebApi}/account/team`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${tokenInfo.accessToken}`,
    },
  });

  const headings = useMemo(() => {
    if (!headingsConfig[0] || !headingsConfig[7]) {
      throw 'headings config needs index 0 and 7';
    }

    headingsConfig[0].name = terminologyConfig?.thirdPartySingularNaming;
    headingsConfig[3].name = `Linked ${terminologyConfig?.thirdPartyPluralNaming}`;
    const users = (Array.isArray(usersData) ? usersData : []) as RegisteredClientUsers[];

    headingsConfig[2].filter = {
      ...headingsConfig[2].filter,
      options: sortedCountries,
    };

    const possibleUsersOptions = users.map((user) => ({
      name: `${user.firstName} ${user.lastName}`,
      value: user.id.toString(),
    }));

    if (Array.isArray(usersData)) {
      // enable filter when userData is available
      headingsConfig[7].name = terminologyConfig?.internalOwnerPluralNaming;
      headingsConfig[7].filter = {
        title: 'Filter by internal owners',
        field: 'internalOwnerIds',
        options: [
          { name: 'None', value: '00000000-0000-0000-0000-000000000000' },
          ...possibleUsersOptions,
        ],
        height: '280px',
        width: '300px',
      };
    }

    return [...headingsConfig];
  }, [usersData, terminologyConfig, sortedCountries]);

  const sortingHeadingOrder = useCallback((sortingIndex, headings, isDescending) => {
    if (sortingIndex >= 0 && headings?.[sortingIndex]?.field) {
      return { sortType: headings?.[sortingIndex]?.field, isDescending };
    }
    return null;
  }, []);

  const parseThirdPartyOwners = (thirdPartyOwners: string[]) => {
    const [first, ...restOfOwners] = thirdPartyOwners;
    return (
      <>
        <span title={first}>{`${ellipsisText(first, 20) ?? ''}${
          restOfOwners.length > 0 ? ',' : ''
        } `}</span>
        <UsersCounter names={restOfOwners} />
      </>
    );
  };

  const {
    data: thirdPartySearchData,
    loading: thirdPartySearchLoading,
    error: thirdPartySearchError,
    trigger: thirdPartySearchFetchLazy,
  } = useFetch(`${vantageWebApi}/thirdParty/search`, {
    method: 'POST',
    lazy: true,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${tokenInfo.accessToken}`,
    },
  });

  useEffect(() => {
    if (thirdPartySearchData && thirdPartySearchData?.results) {
      setCurrentPage(thirdPartySearchData.currentPage);
      setResultData(thirdPartySearchData.results);
    }
  }, [thirdPartySearchData]);

  useEffect(() => {
    if (!usersData) {
      return;
    }
    // handle sessionStorage logic
    if (debouncedSearchTerm?.length === 1) {
      return;
    }

    const controller = new AbortController();

    thirdPartySearchFetchLazy({
      body: JSON.stringify({
        page: filters.pageNumber,
        pageSize: 20,
        query: debouncedSearchTerm,
        filter: {
          ...filters,
        },
        sort: sortingHeadingOrder(sortingIndex, headings, isDescending),
        showAll: showAll,
      }),
      signal: controller.signal,
    });

    sessionStorage.setItem('thirdPartyFilters', JSON.stringify({ ...filters, pageNumber: 1 }));
    sessionStorage.setItem(
      'thirdPartySort',
      JSON.stringify({
        sortingIndex,
        isDescending,
      })
    );

    //handle FilterCountLogic
    const filterCount: { [key: string]: number } = {};

    Object.keys(filters).forEach((key) => {
      if (Array.isArray(filters[key])) {
        filterCount[key] = filters[key].length;
      }
    });

    setSelectedFiltersCount(filterCount);

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearchTerm,
    vantageWebApi,
    showAll,
    sortingIndex,
    headings,
    isDescending,
    sortingHeadingOrder,
    usersData,
    filters,
  ]);

  useEffect(() => {
    const storedFilters = sessionStorage.getItem('thirdPartyFilters');
    if (storedFilters) {
      setFilters(JSON.parse(storedFilters));
    }
    const storedSort = sessionStorage.getItem('thirdPartySort');
    if (storedSort) {
      const { sortingIndex: storedSortingIndex, isDescending: storedIsDescending } =
        JSON.parse(storedSort);
      setSortingIndex(storedSortingIndex);
      setIsDescending(storedIsDescending);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateResultsAfterDelete = () => {
    if (!resultData) {
      return;
    }
    const newResultData = resultData.filter((thirdParty) => {
      if (thirdParty.id !== thirdPartyToBeDeleted) {
        return thirdParty;
      }
    });
    setResultData(newResultData);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setThirdPartyToBeDelete(null);
  };

  const setTableOrder = ({ index }: { index: number }) => {
    if (sortingIndex === index) {
      if (isDescending === false) {
        // Change order direction.
        setIsDescending(true);
      } else {
        // Remove any ordering.
        setIsDescending(false);
        setSortingIndex(-1);
      }
      return;
    }
    setIsDescending(false);
    setSortingIndex(index);
  };

  const canDeleteThirdParties: boolean = hasPermission(GatewayPermissions.DeleteThirdParty);

  const canEditThirdParties: boolean = hasPermission(GatewayPermissions.EditThirdParty);

  const canViewTaskList: boolean = hasPermission(GatewayPermissions.ViewTaskList);

  const hasActions: boolean = canDeleteThirdParties || canEditThirdParties;

  type ActionMenuActions = Array<ActionMenuAction>;

  function getPermittedActions(id: string): ActionMenuActions {
    const actions = [];

    if (canEditThirdParties) {
      actions.push({
        name: 'Edit',
        onClick: () => history.push(`/third-party/edit/${id}`),
      });
    }

    if (canDeleteThirdParties) {
      actions.push({
        name: 'Delete',
        onClick: () => {
          setThirdPartyToBeDelete(id);
          setDeleteModalOpen(true);
        },
      });
    }

    return actions;
  }

  const getThirdPartyIcon = (type) => {
    switch (type) {
      case ThirdPartyType.Individual:
        return 'user';
      case ThirdPartyType.Business:
        return 'company';
      default:
        return 'unknown';
    }
  };

  const getDueDiligenceAbbreviations = (dueDiligenceTypes: string[]): JSX.Element[] | null => {
    if (!dueDiligenceTypes || dueDiligenceTypes.length === 0) return null;

    const result: JSX.Element[] = [];

    if (dueDiligenceTypes.some((type) => type.includes('Screening'))) {
      result.push(<Abbreviation key="Screening" abbreviation="Screening" outline />);
    }

    if (dueDiligenceTypes.some((type) => type.includes('DueDiligence'))) {
      result.push(<Abbreviation key="DueDiligence" abbreviation="DueDiligence" outline />);
    }

    return result.length > 0 ? result : null;
  };

  const handleTooltipClose = () => {
    setTooltipVisible(false);
    localStorage.setItem('show-actions-guide-tooltip', JSON.stringify(false));
  };

  return (
    <AppLayout bgColor="whiteSolid">
      <FilterContext.Provider value={[filters, setFilters]}>
        <PageTitle title={`${terminologyConfig?.thirdPartySingularNaming} manager`}></PageTitle>
        <div style={{ marginBottom: '1.5rem' }}>
          <ChildContainer>
            <div>
              <Search
                ariaLabel={`Search ${terminologyConfig?.thirdPartyPluralNaming.toLowerCase()}`}
                onChange={(event) => {
                  setSearchTerm(event.currentTarget.value);
                  setFilters({ ...filters, pageNumber: 1 });
                }}
              />
              {hasPermission(GatewayPermissions.CreateThirdParty) && (
                <Link to="/third-party/create" style={{ paddingLeft: '1.25rem' }}>
                  Create new {terminologyConfig?.thirdPartySingularNaming}
                </Link>
              )}
            </div>
          </ChildContainer>

          <ChildContainer>
            <ButtonToggle>
              <div>
                {isAdmin && (
                  <Button
                    type="button"
                    style={{ borderRadius: '0.3rem 0rem 0rem 0.3rem' }}
                    disabled={!showAll ? true : false}
                    onClick={() => {
                      setShowAll(!showAll);
                      setCurrentPage(1);
                      setFilters({ ...filters, pageNumber: 1 });
                    }}
                  >
                    My {terminologyConfig?.thirdPartyPluralNaming}
                  </Button>
                )}
                {isAdmin && (
                  <Button
                    type="button"
                    style={{ borderRadius: '0rem 0.3rem 0.3rem  0rem' }}
                    disabled={showAll ? true : false}
                    onClick={() => {
                      setShowAll(!showAll);
                      setCurrentPage(1);
                      setFilters({ ...filters, pageNumber: 1 });
                    }}
                  >
                    All {terminologyConfig?.thirdPartyPluralNaming}
                  </Button>
                )}
              </div>
              <div>
                <Tooltip
                  trigger="click"
                  bodyStyles={'padding: 10px; padding-bottom: 10px;'}
                  tooltipText={
                    (
                      <>
                        You can now search for a report /<br /> screening here
                      </>
                    ) as any
                  }
                  placement="top"
                  modifiers={[
                    {
                      name: 'offset',
                      options: {
                        offset: [-100, 20],
                      },
                    },
                  ]}
                  tooltipShown={tooltipVisible}
                  onClose={handleTooltipClose}
                >
                  <Button
                    type="button"
                    onClick={() => {
                      setDrawerOpen(true);
                    }}
                  >
                    Actions
                  </Button>
                </Tooltip>
              </div>
            </ButtonToggle>
          </ChildContainer>
        </div>
        <Table
          headings={headings}
          showPlaceholder={
            thirdPartySearchLoading || thirdPartySearchError || usersLoading || usersError
          }
          onHeadingClick={setTableOrder}
          sortedColumn={sortingIndex}
          selectedFiltersCount={selectedFiltersCount}
        >
          {resultData.map(
            ({
              alternativeName,
              createdDate,
              dueDiligenceTypes = [],
              id,
              internalId,
              name,
              nextReviewDate,
              primaryCountry,
              relatedThirdPartiesCount,
              taskCount,
              thirdPartyApprovals = [],
              thirdPartyOwners = [],
              type,
            }) => {
              return (
                <tr key={id}>
                  <td>
                    <ThirdPartyName>
                      <ThirdpartyNameLink to={`/third-party/profile/${id}`}>
                        {name || alternativeName || id}
                      </ThirdpartyNameLink>
                      <Callout
                        name={`Additional information for ${name}`}
                        direction="right"
                        trigger={() => (
                          <Indicator
                            name={name}
                            icon={getThirdPartyIcon(type)}
                            value={canViewTaskList ? taskCount : 0}
                          />
                        )}
                        maxHeight="400px"
                      >
                        {canViewTaskList && (
                          <ThirdPartyDetail>
                            <h3>{name}</h3>
                            <div>
                              {taskCount > 0 ? (
                                <p>
                                  You have Outstanding Actions to solve regarding the Third Party.
                                </p>
                              ) : (
                                <p>No Pending Tasks</p>
                              )}
                              <Link to={`/third-party/profile/${id}`}>View details</Link>
                            </div>
                          </ThirdPartyDetail>
                        )}
                      </Callout>
                    </ThirdPartyName>
                  </td>
                  <td>{internalId}</td>
                  <td>{primaryCountry && <Country countryCode={primaryCountry} fullWidth />}</td>
                  <td>
                    <ThirdPartyLinkageDetails
                      id={id}
                      relatedThirdPartiesCount={relatedThirdPartiesCount}
                    />
                  </td>
                  <td>{getDueDiligenceAbbreviations(dueDiligenceTypes)}</td>
                  <td>
                    {thirdPartyApprovals &&
                      thirdPartyApprovals.length > 0 &&
                      thirdPartyApprovals[0].approvalStatus}
                  </td>
                  <td>
                    {thirdPartyApprovals && thirdPartyApprovals.length > 0 && (
                      <RiskRating risk={thirdPartyApprovals[0].riskRating} />
                    )}
                  </td>

                  <td>{parseThirdPartyOwners(thirdPartyOwners)}</td>
                  <td>{createdDate && <Date dateTime={createdDate} />}</td>
                  <td>{nextReviewDate && <Date dateTime={nextReviewDate} />}</td>
                  <ActionMenuWrapper>
                    {hasActions && (
                      <ActionMenu actions={getPermittedActions(id)} name={`Manage ${name}`} />
                    )}
                  </ActionMenuWrapper>
                </tr>
              );
            }
          )}
        </Table>
        {thirdPartySearchData && thirdPartySearchData?.pageCount > 1 && (
          <PaginationWrapper>
            <Pagination
              setCurrentPage={setCurrentPage}
              currentPage={currentPage}
              totalPages={thirdPartySearchData.pageCount || 1}
              onPageChange={({ currentPage }) => {
                setFilters({ ...filters, pageNumber: currentPage });
              }}
            />
          </PaginationWrapper>
        )}
        <ReportActionsDrawer
          thirdpartySearchTerm={searchTerm}
          isDownloadEnabled={!thirdPartySearchLoading}
          showAllThirdparties={showAll}
          isLinkedThirdpartiesFeatureEnabled={isLinkedThirdpartiesFeatureEnabled}
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onProceed={() => {
            setDrawerOpen(false);
          }}
          onCancel={() => setDrawerOpen(false)}
          sort={sortingHeadingOrder(sortingIndex, headings, isDescending)}
        />
      </FilterContext.Provider>
      {canDeleteThirdParties && (
        <ThirdPartyDeleteModal
          isOpen={deleteModalOpen}
          onClose={closeDeleteModal}
          thirdPartyId={thirdPartyToBeDeleted}
          updateResults={updateResultsAfterDelete}
        ></ThirdPartyDeleteModal>
      )}
    </AppLayout>
  );
};

export default withAITracking(reactPlugin, ThirdParty);
